import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { DatePipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { forkJoin, map, Observable, switchMap } from 'rxjs';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatTableModule } from '@angular/material/table';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';

import { DfService } from '../../../../core/df/df.service';
import { AuthService } from '../../../../core/auth/auth.service';
import { aiConsentBoxNeeded } from '../../../../core/attestations/ai-consent.types';
import {
  DebtorRelationship,
  DfFacilityDetail,
  DfFacilityDocType,
  DfFacilitySubmitResponse,
  Invoice,
} from '../../../../core/df/df.types';
import { EntityService } from '../../../../core/entity/entity.service';
import { EntityDetail } from '../../../../core/entity/entity.types';
import { IconComponent } from '../../../../shared/icon/icon';
import { StatusChipComponent } from '../../../../shared/status-chip/status-chip';
import {
  ConfirmDialogComponent,
  ConfirmDialogData,
} from '../../../../shared/confirm-dialog/confirm-dialog';
import { AiConsentWithdrawComponent } from '../../../../shared/ai-consent/ai-consent-withdraw/ai-consent-withdraw';
import { AiConsentCheckboxComponent } from '../../../../shared/ai-consent/ai-consent-checkbox/ai-consent-checkbox';
import { ZarPipe } from '../../../../shared/pipes/zar.pipe';
import { DashPipe } from '../../../../shared/pipes/dash.pipe';
import { AgoPipe } from '../../../../shared/pipes/ago.pipe';
import { DocDropType } from '../../shared/doc-drop/doc-drop';
import { DocUploadPanelComponent, PanelDoc } from '../../shared/doc-upload-panel/doc-upload-panel';
import { readError } from '../../../../core/http/read-error';

/** Document buckets shown on the draft-edit page — the same set the DF wizard asks for. */
const DOC_TYPES: DocDropType<DfFacilityDocType>[] = [
  { value: 'BANK_STATEMENT', label: 'Bank statements', hint: 'Last 3–6 months' },
  { value: 'FINANCIALS', label: 'Financial statements', hint: 'Annual or management accounts' },
  { value: 'AGE_ANALYSIS', label: 'Debtors age analysis', hint: 'Your current age analysis' },
  { value: 'ID_DOCUMENT', label: 'ID documents', hint: 'Directors’ IDs' },
  { value: 'OTHER', label: 'Other supporting documents', hint: 'Anything else relevant' },
];

@Component({
  selector: 'app-df-detail',
  imports: [
    ReactiveFormsModule,
    MatCardModule,
    MatButtonModule,
    MatTableModule,
    MatProgressSpinnerModule,
    MatProgressBarModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    RouterLink,
    DatePipe,
    IconComponent,
    StatusChipComponent,
    ZarPipe,
    DashPipe,
    AgoPipe,
    DocUploadPanelComponent,
    AiConsentWithdrawComponent,
    AiConsentCheckboxComponent,
  ],
  templateUrl: './df-detail.html',
  styleUrl: './df-detail.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DfDetailComponent implements OnInit {
  private readonly dfService = inject(DfService);
  private readonly entityService = inject(EntityService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly fb = inject(FormBuilder);
  private readonly snackBar = inject(MatSnackBar);
  private readonly dialog = inject(MatDialog);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly entityId = this.route.snapshot.paramMap.get('id') ?? '';
  private readonly facilityId = this.route.snapshot.paramMap.get('facilityId') ?? '';

  protected readonly facility = signal<DfFacilityDetail | null>(null);
  protected readonly entity = signal<EntityDetail | null>(null);
  protected readonly debtors = signal<DebtorRelationship[]>([]);
  protected readonly invoices = signal<Invoice[]>([]);
  protected readonly isLoading = signal(true);
  protected readonly loadError = signal(false);
  protected readonly isSaving = signal(false);
  protected readonly isSubmitting = signal(false);
  private readonly auth = inject(AuthService);

  /**
   * POPIA s72 — consent to AI-assisted assessment, the BUSINESS's since
   * FLOW-297: asked only when the business has none standing
   * (`aiConsentBoxNeeded`). Not needed when merely responding to a more-info
   * request (no AI call).
   */
  protected readonly aiConsent = signal(false);
  protected readonly showAiConsentBox = computed(() =>
    aiConsentBoxNeeded(this.aiConsentState(), this.auth.hasStandingAiConsent()),
  );
  protected readonly aiConsentOk = computed(() => !this.showAiConsentBox() || this.aiConsent());
  protected readonly isAddingDebtor = signal(false);
  protected readonly isAddingInvoice = signal(false);
  protected readonly isWithdrawing = signal(false);
  protected readonly serverError = signal<string | null>(null);

  protected readonly DOC_TYPES = DOC_TYPES;

  /** Documents already on the facility — seeds the upload panel once. */
  protected readonly initialDocs = computed<PanelDoc<DfFacilityDocType>[]>(
    () => this.facility()?.documents ?? [],
  );

  /** True while the panel has an upload in flight (blocks Submit). Fed by the
   *  panel's inFlightChange output. */
  protected readonly anyInFlight = signal(false);

  // Backend calls the shared upload panel needs, kept as stable arrow fields so
  // the panel's function inputs don't change identity between renders.
  /**
   * AI-processing consent state (FLOW-290). Null until consent is captured at
   * submit — on a draft there is nothing to withdraw and nothing renders.
   */
  protected readonly aiConsentState = computed(() => this.facility()?.ai_consent ?? null);

  /** The withdrawal POST, as a factory so nothing fires until the client confirms. */
  protected readonly withdrawAiConsent = () => this.dfService.withdrawAiConsent(this.facilityId);

  /**
   * Re-read and untick, so the client is asked afresh rather than shown a
   * ticked box over a withdrawn consent. Re-consent goes through submit.
   */
  protected onAiConsentWithdrawn(): void {
    this.aiConsent.set(false);
    this.load();
  }

  protected readonly uploadDoc = (shortId: string, file: File, docType: DfFacilityDocType) =>
    this.dfService.uploadFacilityDocument(shortId, file, docType);
  protected readonly deleteDoc = (shortId: string, docShortId: string) =>
    this.dfService.deleteFacilityDocument(shortId, docShortId);
  protected readonly fetchDocs = (shortId: string) =>
    this.dfService.getFacility(shortId).pipe(map((f) => f.documents ?? []));

  protected readonly canCapture = computed(() => {
    const stage = this.facility()?.application_stage;
    return stage === 'DRAFT' || stage === 'MORE_INFO_REQUIRED';
  });

  protected readonly canSubmit = computed(() => {
    const stage = this.facility()?.application_stage;
    return stage === 'DRAFT' || stage === 'MORE_INFO_REQUIRED';
  });

  /** A draft is unfinished — drives the resume banner, CTA and withdraw link. */
  protected readonly isDraft = computed(() => this.facility()?.application_stage === 'DRAFT');

  /** Responding to a more-info request re-submits without triggering AI, so it
   *  doesn't need AI-processing consent (unlike a first submit). */
  protected readonly isMoreInfo = computed(
    () => this.facility()?.application_stage === 'MORE_INFO_REQUIRED',
  );

  protected readonly isActive = computed(() => this.facility()?.df_status === 'ACTIVE');

  /**
   * BRE-200 Screen 6.2: show the onboarding-completeness bar while the facility
   * is under review (IN_PROGRESS = submitted, not yet activated). Pre-submit
   * there's nothing being assessed; once ACTIVE the gate is met by definition.
   */
  protected readonly showOnboardingProgress = computed(
    () => this.facility()?.df_status === 'IN_PROGRESS',
  );
  protected readonly onboardingPercent = computed(
    () => this.facility()?.onboarding_progress?.percent ?? 0,
  );

  /**
   * BRE-200 DF-OB-06 (FLOW-163) — the client signs the facility agreement.
   * A hard gate: until this is confirmed (or Ops waives it) the facility cannot
   * be activated. Shown in the same under-review window as the progress bar.
   *
   * DF-OB-07 (cession of the debtor book) is deliberately absent — that one is
   * confirmed by Ops, not the client, and never appears on a client surface.
   */
  protected readonly showAgreementConfirm = this.showOnboardingProgress;
  protected readonly agreementSigned = computed(
    () => this.facility()?.df_agreement_signed ?? false,
  );
  protected readonly agreementSignedAt = computed(
    () => this.facility()?.df_agreement_signed_at ?? null,
  );
  protected readonly isSigningAgreement = signal(false);

  protected readonly captureForm = this.fb.group({
    df_intent: ['', Validators.maxLength(2000)],
    requested_limit: this.fb.control<number | null>(null, [Validators.min(1)]),
    trading_history_months: this.fb.control<number | null>(null, [Validators.min(0)]),
  });

  protected readonly addDebtorForm = this.fb.group({
    name: ['', [Validators.required, Validators.maxLength(255)]],
    reg_no: [''],
  });

  /** The invoice itself, part of the submission (§C5.1 gate ten). */
  protected readonly invoiceFile = signal<File | null>(null);
  protected readonly invoiceFileMissing = signal(false);

  protected readonly addInvoiceForm = this.fb.group({
    relationship: ['', Validators.required],
    invoice_number: ['', Validators.required],
    invoice_amount: this.fb.control<number | null>(null, [Validators.required, Validators.min(1)]),
    invoice_date: [''],
    due_date: [''],
    apparent_overdue_reason: [''],
  });

  ngOnInit(): void {
    this.load();
  }

  protected reload(): void {
    this.loadError.set(false);
    this.isLoading.set(true);
    this.load();
  }

  private load(): void {
    forkJoin({
      entity: this.entityService.getEntity(this.entityId),
      facility: this.dfService.getFacility(this.facilityId),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ entity, facility }) => {
          this.entity.set(entity);
          this.facility.set(facility);
          // The upload panel seeds itself from initialDocs() (the facility's
          // documents) once the record's short_id is known.
          this.isLoading.set(false);
          this.cdr.markForCheck();

          if (facility.df_status === 'ACTIVE') {
            this.loadDebtorsAndInvoices();
          }
        },
        error: () => {
          this.isLoading.set(false);
          this.loadError.set(true);
          this.cdr.markForCheck();
        },
      });
  }

  private loadDebtorsAndInvoices(): void {
    forkJoin({
      debtors: this.dfService.getDebtors(this.facilityId),
      invoices: this.dfService.getInvoices(this.facilityId),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ debtors, invoices }) => {
          this.debtors.set(debtors.debtors);
          this.invoices.set(invoices.invoices);
          this.cdr.markForCheck();
        },
        error: () => undefined,
      });
  }

  protected saveCapture(): void {
    if (this.isSaving()) return;
    this.isSaving.set(true);
    this.serverError.set(null);

    const v = this.captureForm.getRawValue();
    this.dfService
      .captureFacility(this.facilityId, {
        df_intent: v.df_intent ?? undefined,
        requested_limit: v.requested_limit != null ? String(v.requested_limit) : undefined,
        trading_history_months: v.trading_history_months ?? undefined,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (f) => {
          this.facility.set(f);
          this.isSaving.set(false);
          this.snackBar.open('Details saved.', 'Dismiss', { duration: 3000 });
          this.cdr.markForCheck();
        },
        error: (err: unknown) => {
          this.isSaving.set(false);
          this.serverError.set(readError(err));
          this.cdr.markForCheck();
        },
      });
  }

  protected submitOrRespond(): void {
    const f = this.facility();
    if (!f || this.isSubmitting()) return;
    const isMoreInfo = f.application_stage === 'MORE_INFO_REQUIRED';
    // A first submit triggers AI assessment and so requires consent; a
    // more-info response does not.
    if (!isMoreInfo && !this.aiConsentOk()) return;
    this.isSubmitting.set(true);
    this.serverError.set(null);

    const ticked = this.showAiConsentBox() && this.aiConsent();
    const call$: Observable<DfFacilitySubmitResponse | DfFacilityDetail> = isMoreInfo
      ? this.dfService.respondFacility(f.short_id)
      : this.dfService.submitFacility(f.short_id, this.aiConsentOk());

    call$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res) => {
        if (ticked && !isMoreInfo) this.auth.markAiConsentGiven();
        const updated = 'facility' in res ? res.facility : res;
        this.facility.set(updated);
        this.isSubmitting.set(false);
        const msg = 'message' in res ? res.message : 'Application updated.';
        this.snackBar.open(msg, 'Dismiss', { duration: 6000 });
        this.cdr.markForCheck();
      },
      error: (err: unknown) => {
        this.isSubmitting.set(false);
        this.serverError.set(readError(err));
        this.cdr.markForCheck();
      },
    });
  }

  /**
   * BRE-200 DF-OB-06 — the client signs the DF facility agreement.
   *
   * Behind a ConfirmDialog because signing is a legally meaningful, one-way act
   * — there is no un-sign. The backend is idempotent, so a stray repeat is
   * harmless, but the dialog keeps the click deliberate.
   */
  protected signFacilityAgreement(): void {
    const f = this.facility();
    if (!f || this.isSigningAgreement()) return;

    const data: ConfirmDialogData = {
      title: 'Sign the facility agreement?',
      message:
        'You are confirming that you have read and signed the Debtors Finance ' +
        'facility agreement. We record the date and time of this confirmation.',
      confirmLabel: 'Sign agreement',
    };

    this.dialog
      .open(ConfirmDialogComponent, { data, width: '440px' })
      .afterClosed()
      .pipe(
        switchMap((confirmed) => {
          if (!confirmed) {
            return [];
          }
          this.isSigningAgreement.set(true);
          this.serverError.set(null);
          this.cdr.markForCheck();
          return this.dfService.confirmDfAgreement(f.short_id);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (updated) => {
          this.facility.set(updated);
          this.isSigningAgreement.set(false);
          this.snackBar.open('Facility agreement signed.', 'Dismiss', { duration: 5000 });
          this.cdr.markForCheck();
        },
        error: (err: unknown) => {
          this.isSigningAgreement.set(false);
          this.serverError.set(readError(err));
          this.cdr.markForCheck();
        },
      });
  }

  /**
   * Withdraw (soft-delete) an unfinished draft facility. The POPIA erasure path
   * for an abandoned facility and its uploaded documents. DRAFT-only, enforced
   * again on the backend.
   */
  protected withdrawDraft(): void {
    const f = this.facility();
    if (!f || this.isWithdrawing()) return;

    const data: ConfirmDialogData = {
      title: 'Withdraw this application?',
      message:
        'This draft and any documents you uploaded will be removed. ' +
        'This cannot be undone — you can always start a new application later.',
      confirmLabel: 'Withdraw application',
      destructive: true,
    };

    this.dialog
      .open(ConfirmDialogComponent, { data, width: '440px' })
      .afterClosed()
      .pipe(
        switchMap((confirmed) => {
          if (!confirmed) {
            return [];
          }
          this.isWithdrawing.set(true);
          this.serverError.set(null);
          this.cdr.markForCheck();
          return this.dfService.withdrawFacility(f.short_id);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          this.snackBar.open('Application withdrawn.', 'Dismiss', { duration: 5000 });
          void this.router.navigate(['/entity', this.entityId, 'df']);
        },
        error: (err: unknown) => {
          this.isWithdrawing.set(false);
          this.serverError.set(readError(err));
          this.cdr.markForCheck();
        },
      });
  }

  protected addDebtor(): void {
    if (this.addDebtorForm.invalid || this.isAddingDebtor()) {
      this.addDebtorForm.markAllAsTouched();
      return;
    }
    this.isAddingDebtor.set(true);

    const v = this.addDebtorForm.getRawValue();
    this.dfService
      .createDebtor(this.facilityId, {
        name: v.name ?? '',
        reg_no: v.reg_no ?? undefined,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (rel) => {
          this.debtors.update((d) => [...d, rel]);
          this.addDebtorForm.reset();
          this.isAddingDebtor.set(false);
          this.snackBar.open('Debtor added.', 'Dismiss', { duration: 3000 });
          this.cdr.markForCheck();
        },
        error: (err: unknown) => {
          this.isAddingDebtor.set(false);
          this.serverError.set(readError(err));
          this.cdr.markForCheck();
        },
      });
  }

  protected pickInvoiceFile(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.item(0) ?? null;
    this.invoiceFile.set(file);
    this.invoiceFileMissing.set(false);
  }

  protected addInvoice(): void {
    const file = this.invoiceFile();
    if (this.addInvoiceForm.invalid || this.isAddingInvoice() || !file) {
      this.addInvoiceForm.markAllAsTouched();
      this.invoiceFileMissing.set(!file);
      return;
    }
    this.isAddingInvoice.set(true);
    this.serverError.set(null);

    const v = this.addInvoiceForm.getRawValue();
    this.dfService
      .createInvoice(
        this.facilityId,
        {
          relationship: v.relationship ?? '',
          invoice_number: v.invoice_number ?? '',
          invoice_amount: String(v.invoice_amount),
          invoice_date: v.invoice_date ?? undefined,
          due_date: v.due_date ?? undefined,
          apparent_overdue_reason: v.apparent_overdue_reason || undefined,
        },
        file,
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (inv) => {
          this.invoices.update((list) => [...list, inv]);
          this.addInvoiceForm.reset();
          this.invoiceFile.set(null);
          (document.querySelector('[data-testid="invoice-file"]') as HTMLInputElement).value = '';
          this.isAddingInvoice.set(false);
          this.snackBar.open('Invoice submitted.', 'Dismiss', { duration: 3000 });
          this.cdr.markForCheck();
        },
        error: (err: unknown) => {
          this.isAddingInvoice.set(false);
          this.serverError.set(readError(err));
          this.cdr.markForCheck();
        },
      });
  }
}
